<script setup>
import { computed, ref, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import KanbanColumn from 'dashboard/components-next/Kanban/KanbanColumn.vue';

const props = defineProps({
  stages: { type: Array, required: true },
  columns: { type: Object, required: true },
  cardsForStage: { type: Function, required: true },
  isFiltered: { type: Boolean, default: false },
  isSearching: { type: Boolean, default: false },
});

const emit = defineEmits(['move', 'loadMore']);

const { t } = useI18n();

// Drag reordering mutates the raw per-column array directly (Sortable
// splices it in place). That's only meaningful when the board is showing
// the raw, unfiltered, server-fetched list — once a client-side filter,
// client-side sort, or search view reshapes what's displayed, dragging a
// position in that reshaped view wouldn't map back to a sane stage change.
const dragEnabled = computed(() => !props.isFiltered && !props.isSearching);

const activeMobileStage = ref(props.stages[0]);
const desktopColumnRefs = ref({});

// Tailwind's flex/min-h-0/percentage-height chain doesn't reliably resolve
// a bounded height this many levels deep in every browser, so the columns
// never knew when to stop growing and show their own scrollbar. Measuring
// the real available space directly sidesteps that entirely.
const desktopRowRef = ref(null);
const desktopRowHeight = ref(null);

const updateDesktopRowHeight = () => {
  if (!desktopRowRef.value) return;
  const top = desktopRowRef.value.getBoundingClientRect().top;
  desktopRowHeight.value = Math.max(window.innerHeight - top - 12, 200);
};

let resizeObserver = null;

onMounted(() => {
  nextTick(updateDesktopRowHeight);
  window.addEventListener('resize', updateDesktopRowHeight);
  if (window.ResizeObserver) {
    resizeObserver = new ResizeObserver(updateDesktopRowHeight);
    resizeObserver.observe(document.body);
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateDesktopRowHeight);
  resizeObserver?.disconnect();
});

const setDesktopColumnRef = (stage, el) => {
  if (el) desktopColumnRefs.value[stage] = el;
};

const handleMove = ({ contact, fromStage, toStage }) => {
  emit('move', { contact, fromStage, toStage });
};

const scrollToStage = stage => {
  activeMobileStage.value = stage;
  desktopColumnRefs.value[stage]?.scrollIntoView({
    behavior: 'smooth',
    inline: 'start',
    block: 'nearest',
  });
};

defineExpose({ scrollToStage });
</script>

<template>
  <div class="flex flex-col w-full h-full min-h-0">
    <p
      v-if="!dragEnabled"
      class="flex-shrink-0 px-1 pb-2 text-xs text-n-slate-10"
    >
      {{ t('KANBAN.DRAG_DISABLED_HINT') }}
    </p>

    <!-- Mobile: one stage at a time via a pill selector, no cramped columns -->
    <div class="flex flex-col flex-1 min-h-0 md:hidden">
      <div class="flex flex-shrink-0 gap-1.5 pb-3 overflow-x-auto">
        <button
          v-for="stage in stages"
          :key="stage"
          class="flex-shrink-0 px-3 py-1.5 text-sm rounded-full whitespace-nowrap transition-colors"
          :class="
            activeMobileStage === stage
              ? 'bg-n-brand text-white'
              : 'bg-n-alpha-2 text-n-slate-11'
          "
          @click="activeMobileStage = stage"
        >
          {{ stage }}
          <span class="ml-1 opacity-80">{{ columns[stage].count }}</span>
        </button>
      </div>
      <KanbanColumn
        :stage="activeMobileStage"
        :contacts="
          dragEnabled
            ? columns[activeMobileStage].contacts
            : cardsForStage(activeMobileStage)
        "
        :count="columns[activeMobileStage].count"
        :is-loading="columns[activeMobileStage].isLoading"
        :has-more="
          columns[activeMobileStage].hasMore && !isFiltered && !isSearching
        "
        :drag-enabled="false"
        full-width
        class="flex-1 min-h-0"
        @load-more="emit('loadMore', $event)"
      />
    </div>

    <!-- Desktop / tablet: full horizontal board -->
    <div
      ref="desktopRowRef"
      class="flex-1 hidden min-h-0 gap-[18px] pb-2 overflow-x-auto md:flex items-stretch"
      :style="desktopRowHeight ? { height: `${desktopRowHeight}px` } : {}"
    >
      <KanbanColumn
        v-for="stage in stages"
        :key="stage"
        :ref="el => setDesktopColumnRef(stage, el?.$el)"
        :stage="stage"
        :contacts="dragEnabled ? columns[stage].contacts : cardsForStage(stage)"
        :count="columns[stage].count"
        :is-loading="columns[stage].isLoading"
        :has-more="columns[stage].hasMore && !isFiltered && !isSearching"
        :drag-enabled="dragEnabled"
        @move="handleMove"
        @load-more="emit('loadMore', $event)"
      />
    </div>
  </div>
</template>
